import React, { useState, useEffect } from 'react';
import { Trash2, Edit, Plus, X, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { admin } from '../api';

const ManagePlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  // Form state for new/edit plan
  const [formData, setFormData] = useState({
    plan_name: '',
    cost: 0,
    credits_per_month: 0,
    description: '',
    features: [],
    is_active: true,
    is_hidden: false,
    stripe_product_id: '',
    stripe_price_id: ''
  });
  
  // Feature input state
  const [featureInput, setFeatureInput] = useState('');

  // Fetch plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  // Function to fetch plans
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await admin.getPlans();
      setPlans(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : 
              name === 'cost' || name === 'credits_per_month' ? parseFloat(value) : 
              value
    });
  };

  // Add a feature to the features array
  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  // Remove a feature from the features array
  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  // Start editing a plan
  const startEditing = (plan) => {
    setEditingPlanId(plan.id);
    setFormData({
      plan_name: plan.name,
      cost: plan.price,
      credits_per_month: plan.credits_per_month,
      description: plan.description || '',
      features: plan.features || [],
      is_active: plan.is_active,
      is_hidden: plan.is_hidden || false,
      stripe_product_id: plan.stripe_product_id || '',
      stripe_price_id: plan.stripe_price_id || ''
    });
    setShowAddForm(false);
  };

  // Cancel editing or adding
  const cancelEdit = () => {
    setEditingPlanId(null);
    setShowAddForm(false);
    resetForm();
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      plan_name: '',
      cost: 0,
      credits_per_month: 0,
      description: '',
      features: [],
      is_active: true,
      is_hidden: false,
      stripe_product_id: '',
      stripe_price_id: ''
    });
    setFeatureInput('');
  };

  // Submit handler for creating or updating a plan
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingPlanId) {
        // Update existing plan
        await admin.updatePlan(editingPlanId, formData);
        setSuccessMessage('Plan updated successfully');
      } else {
        // Create new plan
        await admin.createPlan(formData);
        setSuccessMessage('Plan created successfully');
      }
      
      // Reset form and fetch updated plans
      resetForm();
      setEditingPlanId(null);
      setShowAddForm(false);
      fetchPlans();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving plan:', error);
      setError(error.response?.data?.detail || 'Failed to save plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle plan deletion
  const deletePlan = async (planId) => {
    try {
      await admin.deletePlan(planId);
      setSuccessMessage('Plan deleted successfully');
      fetchPlans();
      setDeleteConfirmation(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting plan:', error);
      setError(error.response?.data?.detail || 'Failed to delete plan. Please try again.');
    }
  };

  // Toggle plan active status
  const togglePlanStatus = async (planId, currentStatus) => {
    try {
      await admin.togglePlanStatus(planId);
      setSuccessMessage(`Plan ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchPlans();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error toggling plan status:', error);
      setError(error.response?.data?.detail || 'Failed to update plan status. Please try again.');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Manage Subscription Plans</h1>
        {!showAddForm && editingPlanId === null && (
          <button
            onClick={() => {
              setShowAddForm(true);
              resetForm();
            }}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" /> Add New Plan
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {successMessage}
        </div>
      )}

      {/* Plan Form (Add/Edit) */}
      {(showAddForm || editingPlanId !== null) && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">{editingPlanId ? 'Edit Plan' : 'Add New Plan'}</h2>
            <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name *
                </label>
                <input
                  type="text"
                  name="plan_name"
                  value={formData.plan_name}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price ($) *
                </label>
                <input
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credits Per Month *
                </label>
                <input
                  type="number"
                  name="credits_per_month"
                  value={formData.credits_per_month}
                  onChange={handleInputChange}
                  min="-1"
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">Use -1 for unlimited credits</p>
              </div>
              
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Options</label>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                      Active
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_hidden"
                      name="is_hidden"
                      checked={formData.is_hidden}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_hidden" className="ml-2 block text-sm text-gray-700">
                      Hidden (only available via invitations)
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stripe Product ID
              </label>
              <input
                type="text"
                name="stripe_product_id"
                value={formData.stripe_product_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stripe Price ID
              </label>
              <input
                type="text"
                name="stripe_price_id"
                value={formData.stripe_price_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Features
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  placeholder="Add a feature"
                  className="flex-grow border border-gray-300 rounded-l px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-r"
                >
                  Add
                </button>
              </div>
              
              {formData.features.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Current Features:</h4>
                  <ul className="space-y-1">
                    {formData.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm bg-gray-50 px-2 py-1 rounded">
                        <span className="flex-grow">{feature}</span>
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Plan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Plans List */}
      {loading && plans.length === 0 ? (
        <div className="text-center py-12">
          <svg className="animate-spin h-8 w-8 text-emerald-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-500">Loading plans...</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No plans found. Create your first plan to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stripe</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plans.map((plan) => (
                <tr key={plan.id} className={`${!plan.is_active ? 'bg-gray-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{plan.name}</div>
                    {plan.is_hidden && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Hidden
                      </span>
                    )}
                    {plan.description && (
                      <div className="text-sm text-gray-500 mt-1 max-w-xs truncate" title={plan.description}>
                        {plan.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(plan.price)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {plan.credits_per_month === -1 ? 'Unlimited' : plan.credits_per_month}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {plan.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-900">
                      {plan.stripe_product_id ? (
                        <div className="mb-1">
                          <span className="font-medium">Product:</span> 
                          <span className="font-mono bg-gray-100 px-1 rounded" title={plan.stripe_product_id}>
                            {plan.stripe_product_id.length > 10 ? plan.stripe_product_id.substring(0, 10) + '...' : plan.stripe_product_id}
                          </span>
                        </div>
                      ) : (
                        <div className="mb-1 text-red-500">No Product ID</div>
                      )}
                      {plan.stripe_price_id ? (
                        <div>
                          <span className="font-medium">Price:</span> 
                          <span className="font-mono bg-gray-100 px-1 rounded" title={plan.stripe_price_id}>
                            {plan.stripe_price_id.length > 10 ? plan.stripe_price_id.substring(0, 10) + '...' : plan.stripe_price_id}
                          </span>
                        </div>
                      ) : (
                        <div className="text-red-500">No Price ID</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => togglePlanStatus(plan.id, plan.is_active)}
                        className={`text-xs ${
                          plan.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                        } border px-2 py-1 rounded`}
                      >
                        {plan.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        onClick={() => startEditing(plan)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => setDeleteConfirmation(plan.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this plan? This action cannot be undone, and may affect users currently subscribed to this plan.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deletePlan(deleteConfirmation)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePlansPage;
